import { ExternalLink } from "lucide-react";
import { usePressArticles } from "@/hooks/use-press-articles";
import { Container, Skeleton } from "@/components/atoms";

/**
 * Press coverage — "As Seen In".
 *
 * Dr. Burns, 2026-09-01: "I did not even notice this down at the bottom - is there a way to
 * SHOWCASE this better?" She was right on both counts. It sat at position 10 of 12, below the
 * testimonials, and it rendered ONLY the publication name at text-xs in a low-contrast marquee.
 * The `headline` field has always been on PressArticle and was never displayed, so the one thing
 * that makes a mention worth reading — what the piece actually said about her — was invisible.
 *
 * Two changes: it moved up to sit directly under the hero (press is social proof and belongs high),
 * and it now shows the headline at a readable size.
 *
 * A marquee needs a crowd to look deliberate. With a handful of articles an infinite scroll of two
 * duplicated items reads as a bug, so ≤4 renders as a static centred row and the ticker is kept for
 * when the list actually grows.
 */
export function PressCoverage() {
  const { data: articles = [], isLoading } = usePressArticles();

  /*
   * CLS — this section returning `null` while it fetched was fine at position 10, and became the
   * single worst layout shift on the site the moment it moved to position 4. Measured live at
   * 390x844 after the move: the homepage scored CLS 1.0133, from ONE shift of 1.0 at 891ms — the
   * whole viewport, because everything below the hero was shoved down when the articles landed.
   * That was a regression introduced by this component's own relocation, in the same session.
   *
   * Reserving the height with card-shaped skeletons costs nothing and removes it. Still returns
   * null once we KNOW there is nothing to show, which is a real collapse but a rare and far
   * smaller one than popping the section in from zero on every cold load.
   */
  if (isLoading) {
    return (
      <section className="overflow-hidden border-y border-night-blue/10 bg-medical-grey py-7">
        <Container>
          <p className="mb-5 text-center font-condensed text-xs font-bold uppercase tracking-widest text-night-blue/60">
            As Seen In
          </p>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton
                key={i}
                className="h-[74px] rounded-soft sm:max-w-sm sm:flex-1"
              />
            ))}
          </div>
        </Container>
      </section>
    );
  }

  if (!articles.length) return null;

  const marquee = articles.length > 4;
  const items = marquee ? [...articles, ...articles] : articles;

  return (
    <section className="overflow-hidden border-y border-night-blue/10 bg-medical-grey py-7">
      <Container>
        <p className="mb-5 text-center font-condensed text-xs font-bold uppercase tracking-widest text-night-blue/60">
          As Seen In
        </p>

        <div
          className={
            marquee
              ? "flex select-none gap-0"
              : "flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4"
          }
          style={
            marquee
              ? { animation: "press-ticker 34s linear infinite" }
              : undefined
          }
          onMouseEnter={
            marquee
              ? (e) => (e.currentTarget.style.animationPlayState = "paused")
              : undefined
          }
          onMouseLeave={
            marquee
              ? (e) => (e.currentTarget.style.animationPlayState = "running")
              : undefined
          }
        >
          {items.map((a, i) => (
            <a
              key={`${a.id}-${i}`}
              href={a.url || undefined}
              target={a.url ? "_blank" : undefined}
              rel="noopener noreferrer"
              className={[
                "group flex flex-col gap-1 rounded-soft border border-night-blue/10 bg-white px-5 py-4 transition-colors",
                marquee ? "mr-4 w-[22rem] shrink-0" : "sm:max-w-sm sm:flex-1",
                a.url
                  ? "cursor-pointer hover:border-leaf-green-ink/40"
                  : "pointer-events-none cursor-default",
              ].join(" ")}
              aria-label={
                a.url
                  ? `Read "${a.headline}" in ${a.publication}`
                  : `${a.publication}: ${a.headline}`
              }
            >
              <span className="flex items-center gap-1.5">
                <span className="font-condensed text-xs font-bold uppercase tracking-widest text-leaf-green-ink">
                  {a.publication}
                </span>
                {a.publishedDate && (
                  <span className="text-xs text-night-blue/40">
                    · {a.publishedDate}
                  </span>
                )}
                {a.url && (
                  <ExternalLink
                    size={11}
                    className="shrink-0 text-night-blue/25 transition-colors group-hover:text-leaf-green-ink"
                  />
                )}
              </span>

              {/* The headline is the point. It was never rendered before. */}
              <span className="text-sm font-semibold leading-snug text-night-blue transition-colors group-hover:text-leaf-green-ink">
                {a.headline}
              </span>
            </a>
          ))}
        </div>
      </Container>

      {marquee && (
        <style>{`
          @keyframes press-ticker {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @media (prefers-reduced-motion: reduce) {
            [style*="press-ticker"] { animation: none !important; }
          }
        `}</style>
      )}
    </section>
  );
}
